// @flow

import _ from 'lodash';
// import moment from 'moment';
import moment from 'moment-timezone';
import { store } from 'src/stores/store';
import { DATE_RANGE, OPTION_DATE_PICKER } from 'src/constants/analytics';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import { DATE_FORMAT, DATE_FORMAT_V2 } from 'src/constants/language-setting';
import { AnalyticsSettingsType } from 'src/models/analytics';
import { prettyText, formatPricing } from 'src/helpers';
import { getShop } from 'src/helpers';

/**
 * Build initial data for option modal from filterOption
 *
 * @param  {Object} filterOption = {} if add new filter option, != {} if edit
 *
 * @return {Object} initialValue
 */
export const buildInitialAnalyticsValue = (analyticsSettings: AnalyticsSettingsType) => {
  let initialValue = _.cloneDeep(analyticsSettings);
  // Default value
  const enableOrderRevenue = _.get(analyticsSettings, 'enableOrderRevenue', true);

  initialValue.enableOrderRevenue = enableOrderRevenue;

  const ignoreIPs = analyticsSettings.ignoreIPs
    ? analyticsSettings.ignoreIPs.map(item => ({ value: item, isNew: false }))
    : [];

  initialValue.ignoreIPs = ignoreIPs;

  return initialValue;
};

/**
 * Build data for settings
 *
 * @param  {Object} analyticsSettings
 *
 * @return {Object} value to send API
 */
export const buildAnalyticsValue = analyticsSettings => {
  let result = _.cloneDeep(analyticsSettings);

  //Build ignore IPs
  result.ignoreIPs = analyticsSettings.ignoreIPs
    ? analyticsSettings.ignoreIPs.map(item => item.value)
    : [];

  return result;
};

/**
 * Get store date
 *
 *  * @return {Date} UTC Date
 */
export const getStoreDate = date => {
  // Check type of date is string/object
  const dateValue = typeof date === 'string' ? new Date(date) : date;

  const currentState = store.getState();
  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');
  const appLanguage = _.get(
    currentState,
    'oauth.tenantApp.appLanguage',
    APP_LANGUAGE_ENGLISH_VALUE
  );
  if (timezone) {
    const storeDate = moment
      .tz(dateValue.toISOString(), timezone)
      .format(DATE_FORMAT[appLanguage].number);
    return moment(storeDate, DATE_FORMAT[appLanguage].number).toDate();
  }
  return dateValue;
};

/**
 * Get store date - n days
 * @param {*} dateValue : Fri May 19 2023 16:46:32 GMT+0700 (Indochina Time)
 * @param {*} n: num of days before
 */
export const getStoreDateBefore = (dateValue, n = 1) => {
  return moment(dateValue).subtract(n, 'days').toDate();
};

/**
 * Build date range from dates
 *
 * @param  {string} selectedDateRange the selected range
 *
 *  * @return {Object} save product
 */
export const buildDateRangeFromDate = (fromDate, toDate) => {
  const today = getStoreDate(new Date());

  if (moment(fromDate).isSame(today, 'day') && moment(toDate).isSame(today, 'day')) {
    return 'today';
  }
  const yesterday = moment(today).add(-1, 'days');
  if (moment(fromDate).isSame(yesterday, 'day') && moment(fromDate).isSame(yesterday, 'day')) {
    return 'yesterday';
  }

  if (moment(toDate).isSame(today, 'day')) {
    {
      const last7Days = moment(today).add(-7, 'days');
      if (moment(fromDate).isSame(last7Days, 'day')) {
        return 'last7Days';
      }
      const last30Days = moment(today).add(-30, 'days');
      if (moment(fromDate).isSame(last30Days, 'day')) {
        return 'last30Days';
      }
      const last90Days = moment(today).add(-90, 'days');
      if (moment(fromDate).isSame(last90Days, 'day')) {
        return 'last90Days';
      }
      const last180Days = moment(today).add(-180, 'days');
      if (moment(fromDate).isSame(last180Days, 'day')) {
        return 'last180Days';
      }
      const last365Days = moment(today).add(-365, 'days');
      if (moment(fromDate).isSame(last365Days, 'day')) {
        return 'last365Days';
      }
      const last720Days = moment(today).add(-720, 'days');
      if (moment(fromDate).isSame(last720Days, 'day')) {
        return 'last720Days';
      }
    }
  }

  const lastMonthStart = moment(today).add(-1, 'months').startOf('month');
  const lastMonthEnd = moment(today).add(-1, 'months').endOf('month');
  if (
    moment(fromDate).isSame(lastMonthStart, 'day') &&
    moment(toDate).isSame(lastMonthEnd, 'day')
  ) {
    return 'lastMonth';
  }

  return 'custom';
};

/**
 * Build dates from the date range
 *
 * @param  {string} selectedDateRange the selected range
 *
 *  * @return {Object} save product
 */
export const buildDateFromDateRange = selectedDateRange => {
  // DONE
  const currentState = store.getState();
  const appLanguage = _.get(
    currentState,
    'oauth.tenantApp.appLanguage',
    APP_LANGUAGE_ENGLISH_VALUE
  );
  const formatDateNumber = DATE_FORMAT[appLanguage].number;
  let startDate, endDate, selectedDates, month, year;
  const _today = getStoreDate(new Date());
  const today = moment(_today).add(0, 'days');

  switch (selectedDateRange) {
    // today: only in today
    case 'today': {
      startDate = moment(_today).format(formatDateNumber);
      endDate = moment(_today).format(formatDateNumber);

      month = _today.getMonth() - 1;
      month = month < 0 ? 11 : month;
      year = _today.getFullYear();
      year = month === 11 ? year - 1 : year;

      selectedDates = {
        start: _today,
        end: _today
      };
      break;
    }
    // Yesterday
    case 'yesterday': {
      const yesterday = moment(_today).add(-1, 'days');
      startDate = yesterday.format(formatDateNumber);
      endDate = yesterday.format(formatDateNumber);

      month = yesterday.month() - 1;
      month = month < 0 ? 11 : month;
      year = yesterday.year();
      year = month === 11 ? year - 1 : year;

      selectedDates = {
        start: yesterday.toDate(),
        end: yesterday.toDate()
      };
      break;
    }
    // Last 7 days
    case 'last7Days': {
      const last7Days = moment(today).add(-7, 'days');

      startDate = last7Days.format(formatDateNumber);
      endDate = today.format(formatDateNumber);

      month = today.month() - 1;
      month = month < 0 ? 11 : month;
      year = today.year();
      year = month === 11 ? year - 1 : year;

      selectedDates = {
        start: last7Days.toDate(),
        end: today.toDate()
      };
      break;
    }
    // Last 30 days
    case 'last30Days': {
      const last30Days = moment(today).add(-30, 'days');

      startDate = last30Days.format(formatDateNumber);
      endDate = today.format(formatDateNumber);

      month = today.month() - 1;
      month = month < 0 ? 11 : month;
      year = today.year();
      year = month === 11 ? year - 1 : year;

      selectedDates = {
        start: last30Days.toDate(),
        end: today.toDate()
      };
      break;
    }
    // next month: from the first to the end of next month
    case 'lastMonth': {
      const lastMonthStart = moment(today).add(-1, 'months').startOf('month');
      const lastMonthEnd = moment(today).add(-1, 'months').endOf('month');

      startDate = lastMonthStart.format(formatDateNumber);
      endDate = lastMonthEnd.format(formatDateNumber);

      month = lastMonthEnd.month() - 1;
      month = month < 0 ? 11 : month;
      year = lastMonthEnd.year();
      year = month === 11 ? year - 1 : year;

      selectedDates = {
        start: lastMonthStart.toDate(),
        end: lastMonthEnd.toDate()
      };
      break;
    }
    // Last 90 days
    case 'last90Days': {
      const last90Days = moment(today).add(-90, 'days');

      startDate = last90Days.format(formatDateNumber);
      endDate = today.format(formatDateNumber);

      month = today.month() - 1;
      month = month < 0 ? 11 : month;
      year = today.year();
      year = month === 11 ? year - 1 : year;

      selectedDates = {
        start: last90Days.toDate(),
        end: today.toDate()
      };
      break;
    }

    // Last 180 days
    case 'last180Days': {
      const last180Days = moment(today).add(-180, 'days');

      startDate = last180Days.format(formatDateNumber);
      endDate = today.format(formatDateNumber);

      month = today.month() - 1;
      month = month < 0 ? 11 : month;
      year = today.year();
      year = month === 11 ? year - 1 : year;

      selectedDates = {
        start: last180Days.toDate(),
        end: today.toDate()
      };
      break;
    }

    // Last 365 days
    case 'last365Days': {
      const last365Days = moment(today).add(-365, 'days');

      startDate = last365Days.format(formatDateNumber);
      endDate = today.format(formatDateNumber);

      month = today.month() - 1;
      month = month < 0 ? 11 : month;
      year = today.year();
      year = month === 11 ? year - 1 : year;

      selectedDates = {
        start: last365Days.toDate(),
        end: today.toDate()
      };
      break;
    }
    default: {
      //custom
      startDate = moment(_today).format(formatDateNumber);
      endDate = moment(_today).format(formatDateNumber);

      month = _today.getMonth() - 1;
      month = month < 0 ? 11 : month;
      year = _today.getFullYear();
      year = month === 11 ? year - 1 : year;

      selectedDates = {
        start: _today,
        end: _today
      };
      break;
    }
  }

  return {
    startDate,
    endDate,
    month,
    year,
    selectedDates
  };
};

/**
 *  * Get init date for date picker on analytic page
 * @param {*} feature
 * @param {*} templateKey
 * @returns
 */
export const getInitDate = (feature, templateKey, selectedPlan = '') => {
  // Initial from date, to date, compare
  const today = getStoreDate(new Date());
  const last90Days = getStoreDateBefore(today, 90);
  const last7Days = getStoreDateBefore(today, 7);

  const currentState = store.getState();
  const isSubscriptionV4 = _.get(
    currentState.oauth,
    'tenantApp.settings.subscriptionV4.settings.enable',
    false
  );
  const isSubscriptionV5 = _.get(
    currentState.oauth,
    'tenantApp.settings.subscriptionV5.settings.enable',
    false
  );

  let fromDate =
      selectedPlan?.includes('freemium') || (!isSubscriptionV4 && !isSubscriptionV5)
        ? last7Days
        : last90Days,
    toDate = today,
    compare = false;

  // Get data from local storage
  try {
    const lastOptionChooseDate = localStorage.getItem(`${getShop()}-${feature}-last-option`);
    if (OPTION_DATE_PICKER.includes(lastOptionChooseDate)) {
      const rangeDateCalculate = buildDateFromDateRange(lastOptionChooseDate);
      fromDate = _.get(rangeDateCalculate, 'selectedDates.start');
      toDate = _.get(rangeDateCalculate, 'selectedDates.end');
    } else if (lastOptionChooseDate === 'custom') {
      const fromDateLocalStorage = localStorage.getItem(`${getShop()}-${templateKey}-fromDate`);
      const toDateLocalStorage = localStorage.getItem(`${getShop()}-${templateKey}-toDate`);

      fromDate = fromDateLocalStorage ? new Date(fromDateLocalStorage) : fromDate;
      toDate = toDateLocalStorage ? new Date(toDateLocalStorage) : toDate;
    }

    const compareLocalStorage = localStorage.getItem(`${getShop()}-${templateKey}-compare`);
    compare = compareLocalStorage === 'true';
  } catch (error) {}

  return { fromDate, toDate, compare };
};

/**
 * Build dates from the date range
 *
 * @param  {Date} fromDate the start date
 * @param  {Date} toDate the end date
 * @param  {string} selectedDateRange the selected range
 *
 *  * @return {Object} save product
 */
export const buildCompareDateRange = (fromDate, toDate, selectedDateRange, version = 'v1') => {
  let start, end;
  switch (selectedDateRange) {
    // today: only in today
    case 'today':
    // Yesterday
    // falls through
    case 'yesterday': {
      start = moment(fromDate).add(-1, 'days');
      end = start;
      break;
    }
    // Last 7 days
    case 'last7Days': {
      start = moment(fromDate).add(-7, 'days');
      end = moment(fromDate).add(-1, 'days');

      break;
    }
    // Last 30 days
    case 'last30Days': {
      start = moment(fromDate).add(-30, 'days');
      end = moment(fromDate).add(-1, 'days');
      break;
    }
    // next month: from the first to the end of next month
    case 'lastMonth': {
      start = moment(fromDate).add(-1, 'months').startOf('month');
      end = moment(fromDate).add(-1, 'months').endOf('month');
      break;
    }
    // Last 90 days
    case 'last90Days': {
      start = moment(fromDate).add(-90, 'days');
      end = moment(fromDate).add(-1, 'days');
      break;
    }
    default: {
      //custom
      let daysDiff = moment(toDate).diff(moment(fromDate), 'days') + 1;
      start = moment(fromDate).add(-daysDiff, 'days');
      end = moment(fromDate).add(-1, 'days');
      break;
    }
  }

  switch (version) {
    case 'v1':
      return displayDateRange(start, end);
    case 'v2':
      return displayDateRangeV2(start, end);
    default:
      return displayDateRange(start, end);
  }
};

/**
 * Show display date range
 *
 * @param  {Date} fromDate the start date
 * @param  {Date} toDate the end date
 * @param  {string} selectedDateRange the selected range
 *
 *  * @return {Object} save product
 */
export const displayDateRange = (fromDate, toDate) => {
  // DONE
  const currentState = store.getState();
  const appLanguage = _.get(
    currentState,
    'oauth.tenantApp.appLanguage',
    APP_LANGUAGE_ENGLISH_VALUE
  );
  const formatDate = DATE_FORMAT[appLanguage];
  const start = moment.isMoment(fromDate) ? fromDate : moment(fromDate);
  const end = moment.isMoment(toDate) ? toDate : moment(toDate);

  if (start.diff(end) === 0) {
    return moment(fromDate).format(formatDate.longDate);
  } else {
    if (start.year() === end.year()) {
      return `${start.format(formatDate.shortDate)} - ${end.format(formatDate.longDate)}`;
    } else {
      return `${start.format(formatDate.longDate)} - ${end.format(formatDate.longDate)}`;
    }
  }
};

/**
 * Create collection label from key
 *
 * @param  {string} key
 *
 *  * @return {string} label
 */
export const buildLabelFromKey = key => {
  if (!key) {
    return '';
  }

  let arrayValue = _.split(key, '_');
  arrayValue = _.slice(arrayValue, 2);

  return _.join(arrayValue, ' ');
};

/**
 * Prepare filter option values from API
 *
 * @param  {Object} filterOptionValues the filter option values from API
 *
 *  * @return {Object} filterOptionValues with label format
 */
export const prepareFilterOptionValues = (filterOptionValues, allFilterOptions) => {
  const returnValue = _.map(filterOptionValues, filterOptionValue => {
    const { filterOptions } = filterOptionValue;
    const updatedFilterOptions = _.map(filterOptions, filterOption => {
      // Find associate filter option
      const { key = '' } = filterOption;
      const filterOptionDetails = _.find(allFilterOptions, { filterOptionId: key });

      return {
        ...filterOption,
        // Get filter option key
        key: filterOptionDetails
          ? _.get(filterOptionDetails, 'label')
          : buildLabelFromKey(filterOption.key),
        originalKey: filterOption.key
      };
    });
    return { ...filterOptionValue, filterOptions: updatedFilterOptions };
  });

  return returnValue;
};

/**
 * Prepare collection total filter click from API
 *
 * @param  {Object} collectionsTotalFilterClicks the collection total filter click from API
 *
 *  * @return {Object} collectionsTotalFilterClicks with format to display
 */
export const prepareCollectionsTotalFilterClicks = collectionsTotalFilterClicks => {
  const returnValue = _.map(collectionsTotalFilterClicks, collectionsTotalFilterClick => {
    let { collectionId } = collectionsTotalFilterClick;

    // Get collection label
    const currentState = store.getState();
    const collections = _.get(currentState, `filterData.filterData.collections`, []);
    const collection = _.find(collections, { value: collectionId.toString() }, {});
    const collectionLabel = collection ? collection.label : collectionId;

    return { ...collectionsTotalFilterClick, collectionLabel };
  });

  return returnValue;
};

/* Prepare collection total sale from API
 *
 * @param  {Object} collectionsTotalFilterClicks the collection total filter click from API
 *
 *  * @return {Object} collectionsTotalFilterClicks with format to display
 */
export const prepareCollectionsTotalSales = collectionsTotalSales => {
  const returnValue = _.map(collectionsTotalSales, collectionsTotalSale => {
    let { collectionId = '' } = collectionsTotalSale;

    // Get collection label
    const currentState = store.getState();
    const collections = _.get(currentState, `filterData.filterData.collections`, []);
    const collection = _.find(collections, { value: collectionId.toString() }, {});
    const collectionLabel = collection ? collection.label : collectionId;

    return { ...collectionsTotalSale, collectionLabel };
  });

  return returnValue;
};
/**
 * Display date on tooltip
 *
 * @param  {string} datetime ISOString format
 *
 *  * @return {string} date format
 */
export const prepareDateLabelTooltipOnChart = datetime => {
  // Format date
  const currentState = store.getState();
  // Format date with language
  const appLanguage = _.get(
    currentState,
    'oauth.tenantApp.appLanguage',
    APP_LANGUAGE_ENGLISH_VALUE
  );
  const formatDate = DATE_FORMAT[appLanguage];
  // Check timezone
  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');
  const storeDatetime = timezone ? moment.tz(datetime, timezone) : moment(datetime);
  // Check date range
  const fromDate = _.get(currentState, 'analytics.fromDate', getStoreDate(new Date()));
  const toDate = _.get(currentState, 'analytics.toDate', getStoreDate(new Date()));
  const daydiff = moment(toDate).diff(moment(fromDate), 'days');

  const datetimeFormat =
    daydiff >= 3
      ? storeDatetime.format(formatDate.longDate)
      : storeDatetime.format(formatDate.fullDate);

  return datetimeFormat;
};

/**
 * Display date on axis
 *
 * @param  {string} datetime ISOString format
 *
 *  * @return {string} date format
 */
export const prepareDateLabelAxesOnChart = datetime => {
  // Format date
  const currentState = store.getState();
  const appLanguage = _.get(
    currentState,
    'oauth.tenantApp.appLanguage',
    APP_LANGUAGE_ENGLISH_VALUE
  );
  const formatDate = DATE_FORMAT[appLanguage];
  // Check timezone
  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');
  const storeDatetime = timezone ? moment.tz(datetime, timezone) : moment(datetime);
  // Check date range
  const fromDate = _.get(currentState, 'analytics.fromDate', getStoreDate(new Date()));
  const toDate = _.get(currentState, 'analytics.toDate', getStoreDate(new Date()));
  const daydiff = moment(toDate).diff(moment(fromDate), 'days');

  const datetimeFormat =
    daydiff >= 3 || (daydiff === 2 && storeDatetime.hour() === 0)
      ? storeDatetime.format(formatDate.shortDate)
      : storeDatetime.format('HH:mm A');

  return datetimeFormat;
};

/**
 * Format filter option value for Price, Variant Price and Percent sale having value in range
 *
 * @param  {object} filterOption filter option { key, originalKey, value }
 * @param  {string} formatMoney  format money for pricing
 * @param  {I18n} i18n  i18n object
 *
 * @return {string}              string under new format
 */
export const formatFilterOptionValue = (filterOption, formatMoney, i18n) => {
  if (!filterOption) return '';
  let { originalKey, value } = filterOption;

  const displayValue = (key, value) => {
    // Return value with currency ex: ${{value}} or £{{value}}
    if (key.startsWith('pf_vp_') || key.startsWith('pf_p_')) {
      return formatPricing(value, formatMoney);
    }

    // Return value with percent ex: {{value}}%
    if (key.startsWith('pf_ps_')) {
      return `${value}%`;
    }

    return value;
  };

  if (
    originalKey.startsWith('pf_ps_') ||
    originalKey.startsWith('pf_vp_') ||
    originalKey.startsWith('pf_p_')
  ) {
    if (value.startsWith(':')) {
      return i18n.translate('AdminHTML.Analytics.Helpers.formatFilterOptionValue_Under', {
        nt_value: displayValue(originalKey, value.slice(1))
      });
    }
    if (value.endsWith(':')) {
      return i18n.translate('AdminHTML.Analytics.Helpers.formatFilterOptionValue_Above', {
        nt_value: displayValue(originalKey, value.slice(0, -1))
      });
    }
    const split = value.split(':');
    return split.length === 2
      ? `${displayValue(originalKey, split[0])} - ${displayValue(originalKey, split[1])}`
      : value;
  }

  return value;
};

/**
 * Build date range with limit
 *
 * @param  {string} datetime ISOString format
 *
 *  * @return {string} date format
 */
export const buildDateRangeWithLimitDays = () => {
  // DONE
  const dateRange = [...DATE_RANGE];
  return dateRange;
};

/**
 * Check validate IP
 *
 * @param  {string} IPList
 *
 * @return {string} checked result
 *
 */
export const checkValidateIP = (IP, IPList, i18n) => {
  let value = IP.trim();

  if (value) {
    value = prettyText(value);

    let checkValue = value.match(
      /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/g
    );
    if (_.isEmpty(value) || !checkValue) {
      return {
        error: i18n.translate('AdminHTML.Analytics.Helpers.error1')
      };
    }

    const found = _.find(IPList, item => item.value === value);
    if (_.isEmpty(found)) {
      return {
        IP: value,
        error: ''
      };
    } else {
      return {
        error: i18n.translate('AdminHTML.Analytics.Helpers.error2')
      };
    }
  }

  return {};
};

/**
 * Get max boundary of the chart with limit values
 *
 * @param  {number[]} data
 * @return {number} max boundary
 *
 */
export const getMaxBoundaryOfChartWithLimit = (data: number[], yAxisTickAmount) => {
  if (yAxisTickAmount && yAxisTickAmount === 3) {
    const milestones = [30, 60, 90, 100];
    const maxValue = _.max(data);
    for (let milestone of milestones) {
      if (maxValue < milestone) {
        return milestone;
      }
    }
    return 100;
  }
  const milestones = [10, 20, 40, 60, 80, 100];
  const maxValue = _.max(data);
  for (let milestone of milestones) {
    if (maxValue < milestone) {
      return milestone;
    }
  }
  return 100;
};

/**
 * Get max boundary of the chart
 *
 * @param  {number[]} data
 * @param  {number} tickAmount  Tick amount for each point on the y-axis of the chart
 * @return {number} max boundary
 *
 */
export const getMaxBoundaryOfChart = (data: number[], tickAmount = 4) => {
  if (_.isEmpty(data) || data.every(item => item === 0)) {
    if (tickAmount === 3) {
      return 3000;
    }
    return 1000;
  }
  const maxValue = _.max(data);
  let roundedNumber = roundNumber(maxValue, tickAmount);
  roundedNumber = roundedNumber === 0 ? 100 : roundedNumber;
  for (let i = roundedNumber; ; i += 100) {
    if (i % tickAmount === 0) {
      return i;
    }
  }
};

/**
 * Show display date range version 2
 *
 * @param  {Date} fromDate the start date
 * @param  {Date} toDate the end date
 * @param  {string} selectedDateRange the selected range
 *
 *  * @return {Object} save product
 */
export const displayDateRangeV2 = (fromDate, toDate) => {
  // DONE
  const currentState = store.getState();
  const appLanguage = _.get(
    currentState,
    'oauth.tenantApp.appLanguage',
    APP_LANGUAGE_ENGLISH_VALUE
  );
  const formatDate = DATE_FORMAT_V2[appLanguage];
  const start = moment.isMoment(fromDate) ? fromDate : moment(fromDate);
  const end = moment.isMoment(toDate) ? toDate : moment(toDate);

  if (end.diff(start) > 0) {
    if (start.year() !== end.year()) {
      return `${start.format(formatDate.fromDate.multipleYears)} - ${end.format(
        formatDate.toDate.multipleYears
      )}`;
    }
    if (start.month() !== end.month()) {
      return `${start.format(formatDate.fromDate.multipleMonths)} - ${end.format(
        formatDate.toDate.multipleMonths
      )}`;
    }
    if (start.date() !== end.date()) {
      return `${start.format(formatDate.fromDate.multipleDays)}-${end.format(
        formatDate.toDate.multipleDays
      )}`;
    }
  }
  return moment(fromDate).format(formatDate.oneDay);
};

/**
 * Round the number to hundred unit
 *
 * @param  {number[]} value
 * @return {number} rounded number
 *
 */
function roundNumber(value, tickAmount = 4) {
  if (value <= 20) return 20;
  const integerPart = value.toString().split('.')[0];
  const integerPartLength = integerPart.length - 1;
  const roundTo = Number(`1e${integerPartLength > 1 ? integerPartLength - 1 : integerPartLength}`);
  const b = Math.ceil(value / tickAmount);
  const c = Math.ceil(b / roundTo) * roundTo;
  const d = c * tickAmount;

  return d;
}

export const getLimitDays = tenantApp => {
  const selectedPlan = _.get(tenantApp, 'selectedPlan', '');

  // Optimize repeated includes checks by normalizing plan and caching flags
  const plan = (selectedPlan || '').toLowerCase();
  //extract only texts basic, essential, professional, freemium, launch, convert, accelerate from plan
  const planTypeMatch = plan.match(
    /(basic|essential|professional|freemium|launch|convert|accelerate)/
  );
  const planType = planTypeMatch ? planTypeMatch[0] : '';

  const LIMITS = {
    professional: 365,
    accelerate: 365,
    essential: 180,
    convert: 180,
    basic: 90,
    launch: 90,
    freemium: 30
  };

  const analyticSettings = _.get(tenantApp, 'settings.analyticV2.settings', {});

  if (analyticSettings && !_.isEmpty(analyticSettings)) {
    const foundLimit = [365, 180, 165, 90, 30, 7].find(
      days => analyticSettings[`analytics-${days}-days`]
    );
    if (foundLimit) return foundLimit;
  }

  return LIMITS[planType] || 90;
};
